/**
 * @avidtech6/pact-viewer
 *
 * The human-facing story layer for FreshVibe modules.
 * Renders mockups, tone variants, personality, examples, and constraints
 * from pact-viewer.yml OR the 8 source files of any module.
 *
 * Two output shapes:
 *   - <PactViewer moduleId="button" />  → React component (for the gallery UI)
 *   - <PactViewerStatic moduleId="button" /> → HTML/JSON bundle (for embedding in user apps)
 *
 * Cross-app visibility: the toggle in <PactViewer> lets a VibeCoder see
 * "this is how my app uses button" / "this is how FreshCards uses button"
 * / "this is the gallery canonical version".
 */
import React, { useEffect, useState } from 'react';
import { load as yamlLoad } from 'js-yaml';
const yaml = { load: yamlLoad };
import './pact-viewer.css';

// ============================================================
// Types
// ============================================================

export interface PactViewerData {
  id: string;
  generated_at: string;
  generator_version: string;

  mockups?: {
    default?: { src: string };
    hover?: { src: string };
    mobile?: { src: string };
  };

  simple?: string;
  deep?: string;
  expert?: string;

  tone?: {
    friendly?: string;
    designer?: string;
    technical?: string;
    marketing?: string;
  };

  personality?: {
    archetype?: string;
    mood?: string[];
    energy?: 'low' | 'medium' | 'high';
    palette?: string[];
    voice?: string;
  };

  purpose?: string;
  usage?: {
    when_to_use?: string[];
    when_not_to_use?: string[];
    pairing?: string[];
  };

  examples?: Array<{ title: string; config: Record<string, any> }>;
  constraints?: string[];
  warnings?: string[];
  best_practices?: string[];
}

export interface PactViewerProps {
  moduleId: string;
  source?: 'canonical' | 'app' | 'url';
  sourceUrl?: string;
  tone?: 'friendly' | 'designer' | 'technical' | 'marketing' | 'all';
  includeMockups?: boolean;
  includeExamples?: boolean;
  includeConstraints?: boolean;
  includeCrossApp?: boolean;
  theme?: 'dark' | 'light';
}

// ============================================================
// Data fetching
// ============================================================

const GALLERY_FEED = 'https://gallery.freshvibeapps.com/feed';

async function fetchPactViewerData(
  moduleId: string,
  source: PactViewerProps['source'] = 'canonical',
  sourceUrl?: string,
): Promise<PactViewerData | null> {
  // Try yml first (primary), fall back to source files (extracted)
  const ymlUrl =
    source === 'url' && sourceUrl
      ? sourceUrl
      : source === 'app'
      ? `/local/pact-viewer/${moduleId}.yml`  // consumer app's local copy
      : `${GALLERY_FEED}/recipe/${moduleId}/pact-viewer.yml`;

  try {
    const res = await fetch(ymlUrl);
    if (res.ok) {
      const text = await res.text();
      const parsed = parseYaml(text);
      if ((parsed as any)?._error) {
        throw new Error("yml parse failed: " + (parsed as any)._error);
      }
      return parsed;
    }
  } catch {}

  // Fallback: synthesize from the 8 source files
  // (this is the dynamic path for widgets without a yml yet)
  return synthesizeFromSources(moduleId, source, sourceUrl);
}

function parseYaml(text: string): PactViewerData & { _error?: string } {
  // Use js-yaml for proper YAML parsing. Handles nested objects, arrays,
  // multi-line strings, flow style, and all the other YAML edge cases.
  try {
    const parsed = yaml.load(text) as any;
    return (parsed?.pact_viewer || parsed || {}) as PactViewerData;
  } catch (e: any) {
    console.error("[pact-viewer] yml parse failed:", e?.message, "\nFirst 200 chars of input:", text?.substring(0, 200));
    return { _error: e?.message || "YAML parse failed" } as any;
  }
}

async function synthesizeFromSources(
  moduleId: string,
  source: PactViewerProps['source'],
  sourceUrl?: string,
): Promise<PactViewerData | null> {
  // Read the 8 source files and synthesize a basic viewer on the fly
  const baseUrl =
    source === 'url' && sourceUrl
      ? sourceUrl
      : source === 'app'
      ? `/local/cms-widgets/${moduleId}`
      : `${GALLERY_FEED}/recipe/${moduleId}`;

  try {
    const [moduleJson, codex] = await Promise.all([
      fetch(`${baseUrl}/module.json`).then((r) => r.json()),
      fetch(`${baseUrl}/codex.md`).then((r) => r.text()).catch(() => ''),
    ]);

    return {
      id: moduleId,
      generated_at: new Date().toISOString(),
      generator_version: 'pact-viewer-1.0.0-fallback',
      simple: moduleJson.description || `A ${moduleJson.name || moduleId} module.`,
      deep: codex.slice(0, 500) || `See ${baseUrl}/codex.md for full codex.`,
      expert: `Module id: ${moduleId}, version: ${moduleJson.version}, kind: ${moduleJson.kind || 'unknown'}.`,
      tone: {
        technical: `Module ${moduleId} at ${baseUrl}.`,
      },
      purpose: moduleJson.description,
      examples: [],
      constraints: [],
      warnings: [],
    };
  } catch {
    return null;
  }
}

// ============================================================
// Cross-app discovery
// ============================================================

async function fetchCrossAppVersions(moduleId: string): Promise<Array<{ app: string; url: string; label: string }>> {
  // The cross-app registry: which apps have this module, and where their viewer is
  try {
    const res = await fetch(`${GALLERY_FEED}/cross-app-registry.json`);
    if (res.ok) {
      const registry = await res.json();
      return registry.modules?.[moduleId]?.apps || [];
    }
  } catch {}
  return [
    { app: 'gallery', url: `${GALLERY_FEED}/recipe/${moduleId}/pact-viewer.yml`, label: 'Gallery canonical' },
  ];
}

// ============================================================
// Main React component
// ============================================================

export const PactViewer: React.FC<PactViewerProps> = ({
  moduleId,
  source = 'canonical',
  sourceUrl,
  tone = 'all',
  includeMockups = true,
  includeExamples = true,
  includeConstraints = true,
  includeCrossApp = true,
  theme = 'dark',
}) => {
  const [data, setData] = useState<PactViewerData | null>(null);
  const [crossAppVersions, setCrossAppVersions] = useState<Array<{ app: string; url: string; label: string }>>([]);
  const [activeSource, setActiveSource] = useState<string>('canonical');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchPactViewerData(
      moduleId,
      (activeSource === 'canonical' ? source : 'app') as 'canonical' | 'app' | 'url',
      activeSource === 'canonical' ? sourceUrl : undefined,
    ).then((d) => {
      setData(d);
      setLoading(false);
    });
  }, [moduleId, activeSource, source, sourceUrl]);

  useEffect(() => {
    if (includeCrossApp) {
      fetchCrossAppVersions(moduleId).then(setCrossAppVersions);
    }
  }, [moduleId, includeCrossApp]);

  if (loading) return <div className={`pact-viewer loading theme-${theme}`}>Loading {moduleId}…</div>;
  if (!data) return <div className={`pact-viewer empty theme-${theme}`}>No Pact data for {moduleId}.</div>;

  return (
    <div className={`pact-viewer theme-${theme}`}>
      <header className="pv-header">
        <h2>{data.id}</h2>
        {includeCrossApp && crossAppVersions.length > 1 && (
          <div className="pv-source-toggle">
            <label>How other apps use it:</label>
            <select value={activeSource} onChange={(e) => setActiveSource(e.target.value)}>
              {crossAppVersions.map((v) => (
                <option key={v.app} value={v.app}>{v.label}</option>
              ))}
            </select>
          </div>
        )}
      </header>

      {data.simple && (
        <section className="pv-simple"><h3>The simple version</h3><p>{data.simple}</p></section>
      )}

      {includeMockups && data.mockups && (
        <section className="pv-mockups">
          {data.mockups.default && <img src={data.mockups.default.src} alt={`${data.id} default`} />}
          {data.mockups.hover && <img src={data.mockups.hover.src} alt={`${data.id} hover`} />}
          {data.mockups.mobile && <img src={data.mockups.mobile.src} alt={`${data.id} mobile`} />}
        </section>
      )}

      {data.tone && tone !== 'all' ? (
        <section className="pv-tone"><h3>The {tone} version</h3><p>{data.tone[tone]}</p></section>
      ) : data.tone ? (
        <section className="pv-tone">
          <h3>Four ways to describe it</h3>
          {Object.entries(data.tone).map(([k, v]) => v ? <p key={k}><strong>{k}:</strong> {String(v)}</p> : null)}
        </section>
      ) : null}

      {data.personality && (
        <section className="pv-personality">
          <h3>Personality</h3>
          {data.personality.archetype && <p><em>"{data.personality.archetype}"</em></p>}
          {data.personality.mood && <p>Mood: {data.personality.mood.join(', ')}</p>}
          {data.personality.energy && <p>Energy: {data.personality.energy}</p>}
          {data.personality.voice && <p>Voice: {data.personality.voice}</p>}
        </section>
      )}

      {data.purpose && (
        <section className="pv-purpose"><h3>Purpose</h3><p>{data.purpose}</p></section>
      )}

      {data.usage && (
        <section className="pv-usage">
          {data.usage.when_to_use && data.usage.when_to_use.length > 0 && (
            <>
              <h3>When to use</h3>
              <ul>{data.usage.when_to_use.map((u, i) => <li key={i}>{u}</li>)}</ul>
            </>
          )}
          {data.usage.when_not_to_use && data.usage.when_not_to_use.length > 0 && (
            <>
              <h3>When NOT to use</h3>
              <ul>{data.usage.when_not_to_use.map((u, i) => <li key={i}>{u}</li>)}</ul>
            </>
          )}
        </section>
      )}

      {includeExamples && data.examples && data.examples.length > 0 && (
        <section className="pv-examples">
          <h3>Examples</h3>
          {data.examples.map((ex, i) => (
            <div key={i} className="pv-example">
              <h4>{ex.title}</h4>
              <pre><code>{JSON.stringify(ex.config, null, 2)}</code></pre>
            </div>
          ))}
        </section>
      )}

      {includeConstraints && (
        <>
          {data.constraints && data.constraints.length > 0 && (
            <section className="pv-constraints">
              <h3>Constraints</h3>
              <ul>{data.constraints.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </section>
          )}
          {data.warnings && data.warnings.length > 0 && (
            <section className="pv-warnings">
              <h3>Warnings</h3>
              <ul>{data.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </section>
          )}
          {data.best_practices && data.best_practices.length > 0 && (
            <section className="pv-best">
              <h3>Best practices</h3>
              <ul>{data.best_practices.map((b, i) => <li key={i}>{b}</li>)}</ul>
            </section>
          )}
        </>
      )}

      <footer className="pv-footer">
        <small>Generated {data.generated_at} by {data.generator_version}</small>
      </footer>
    </div>
  );
};

// ============================================================
// Static HTML/JSON bundle (for embedding in user apps)
// ============================================================

export const PactViewerStatic: React.FC<PactViewerProps> = (props) => {
  // Same component, but renders into a portal/static div for embedding
  return <PactViewer {...props} />;
};

// ============================================================
// Hook for advanced usage
// ============================================================

export function usePactViewer(moduleId: string, source: PactViewerProps['source'] = 'canonical') {
  const [data, setData] = useState<PactViewerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchPactViewerData(moduleId, source)
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [moduleId, source]);

  return { data, loading, error };
}

export default PactViewer;
